/* ---------------------------------------------------------------------------
   HAUS UGC
   ---------------------------------------------------------------------------
   Drives both UGC surfaces: the compact PDP block and the full width section.

   Two constraints shape the whole file.

   1. The PDP swaps its markup out from under us. product-swatch-ajax.js
      replaces the product info column on every colourway change, so anything
      that attaches a listener per element leaks a listener per swap. That is
      the same shape as the OOM already fixed on the Three Nails and ASRV PLPs.
      So: listeners are attached once, to document, and matched by delegation.
      The only exception is the IntersectionObserver, which unobserves nodes as
      soon as it sees they are no longer connected.

   2. Nothing downloads until it is needed. Rail videos render with no src at
      all; the observer assigns one when the card is near the viewport, and
      pauses it again when it leaves. A PDP with a UGC rail costs one poster
      image per card on load, not four videos.
   --------------------------------------------------------------------------- */

(function () {
  if (window.__hausUgcInit) return;
  window.__hausUgcInit = true;

  var RAIL = '.haus-ugc__rail';
  var ROOT = '.haus-ugc';

  function closest(el, sel) {
    return el && el.closest ? el.closest(sel) : null;
  }

  /* ---------------------------------------------------------------------
     Arrow state
     --------------------------------------------------------------------- */

  function syncArrows(rail) {
    var root = closest(rail, ROOT);
    if (!root) return;
    // 2px of slack: sub-pixel scroll widths otherwise leave a next arrow
    // pointing at nothing when you are already at the end.
    var max = rail.scrollWidth - rail.clientWidth;
    root.classList.toggle('has-prev', rail.scrollLeft > 2);
    root.classList.toggle('has-next', rail.scrollLeft < max - 2);
  }

  function syncAll() {
    document.querySelectorAll(RAIL).forEach(syncArrows);
  }

  // scroll does not bubble, so capture it on the way down instead of binding
  // one listener per rail.
  document.addEventListener(
    'scroll',
    function (e) {
      var t = e.target;
      if (t && t.nodeType === 1 && t.matches && t.matches(RAIL)) syncArrows(t);
    },
    true
  );

  window.addEventListener('resize', debounce(syncAll, 120));

  function debounce(fn, ms) {
    var id;
    return function () {
      clearTimeout(id);
      id = setTimeout(fn, ms);
    };
  }

  /* ---------------------------------------------------------------------
     Arrow clicks + opening the viewer
     --------------------------------------------------------------------- */

  document.addEventListener('click', function (e) {
    var arrow = closest(e.target, '.haus-ugc__arrow');
    if (arrow) {
      var root = closest(arrow, ROOT);
      var rail = root && root.querySelector(RAIL);
      if (!rail) return;
      // Page by a full viewport of the rail, not by one card. Cards are sized to
      // an exact fraction of the rail (four across), so one clientWidth lands
      // precisely on the start of the next set with nothing half cut.
      var step = rail.clientWidth;
      rail.scrollBy({
        left: arrow.classList.contains('haus-ugc__arrow--next') ? step : -step,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
      return;
    }

    var item = closest(e.target, '.haus-ugc__item');
    if (item) {
      e.preventDefault();
      openViewer(item);
    }
  });


  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Attach sources as <source> children rather than setting video.src, so the
     browser does the choosing. Safari and iOS take the HLS and adapt the
     bitrate down; Chrome and Firefox cannot decode it, skip past, and land on
     the mp4. No UA sniffing, and it degrades correctly if either is missing. */
  function attachSources(video, mp4, hls) {
    if (video.dataset.ugcLoaded === '1') return;
    video.dataset.ugcLoaded = '1';
    while (video.firstChild) video.removeChild(video.firstChild);
    if (hls) {
      var s1 = document.createElement('source');
      s1.src = hls;
      s1.type = 'application/x-mpegURL';
      video.appendChild(s1);
    }
    if (mp4) {
      var s2 = document.createElement('source');
      s2.src = mp4;
      s2.type = 'video/mp4';
      video.appendChild(s2);
    }
    video.load();
  }

  function clearSources(video) {
    video.pause();
    while (video.firstChild) video.removeChild(video.firstChild);
    video.removeAttribute('src');
    video.dataset.ugcLoaded = '';
    // Without the reload the browser keeps buffering the source we just dropped.
    video.load();
  }

  /* ---------------------------------------------------------------------
     Inline playback for the full width rail
     --------------------------------------------------------------------- */

  /* Instagram-style preview: the rail loops only the opening seconds instead of
     the whole clip. Two reasons. It reads better — every card is showing its
     hook rather than drifting into the middle of someone's sentence — and it
     caps the download, because a 3 second window of a ~7Mbps file is all that
     ever buffers.

     The listener is bound to the video element itself, not to document, so it
     is collected along with the element when the PDP swaps its column out.
     Delegation would be the wrong tool here: timeupdate does not bubble. */
  function bindPreviewLoop(v) {
    if (v.dataset.ugcPreviewBound === '1') return;
    var root = closest(v, ROOT);
    var seconds = root ? parseFloat(root.dataset.ugcPreview) : NaN;
    // 0 (or a missing/invalid value) means play the clip through.
    if (!seconds || isNaN(seconds) || seconds <= 0) return;
    v.dataset.ugcPreviewBound = '1';
    v.addEventListener('timeupdate', function () {
      if (v.currentTime >= seconds) v.currentTime = 0;
    });
  }

  /* Two margins, on purpose.

     Until the page has finished loading we only reach 200px ahead, so a rail
     below the fold cannot start pulling multi-megabyte video while the browser
     is still fetching the things that actually render the page.

     Once load has fired and the main thread goes idle, nothing is left to
     compete with, so the margin widens to 800px and the next rail is buffered
     before you scroll to it. Cards already on screen are unaffected either way
     — they start immediately under both margins. */
  var MARGIN_INITIAL = '200px 0px';
  var MARGIN_PREFETCH = '800px 0px';
  var margin = MARGIN_INITIAL;
  var io = null;

  function observer() {
    if (io) return io;
    io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (!v.isConnected) {
            io.unobserve(v);
            return;
          }
          if (entry.isIntersecting) {
            attachSources(v, v.dataset.ugcSrc, v.dataset.ugcHls);
            bindPreviewLoop(v);
            var p = v.play();
            // Autoplay can still be refused (low power mode, data saver).
            // The poster stays up, which is a fine resting state.
            if (p && p.catch) p.catch(function () {});
          } else {
            v.pause();
          }
        });
      },
      { rootMargin: margin, threshold: 0.25 }
    );
    return io;
  }

  /* rootMargin is fixed at construction, so widening it means rebuilding the
     observer and re-observing. Cheap, and it happens once. Videos that already
     have sources keep them — attachSources is guarded. */
  function widenMargin() {
    if (margin === MARGIN_PREFETCH) return;
    margin = MARGIN_PREFETCH;
    if (io) {
      io.disconnect();
      io = null;
    }
    document.querySelectorAll('.haus-ugc video[data-ugc-src]').forEach(function (v) {
      v.dataset.ugcObserved = '';
    });
    scan();
  }

  function afterLoad() {
    if (window.requestIdleCallback) requestIdleCallback(widenMargin, { timeout: 2000 });
    else setTimeout(widenMargin, 500);
  }

  function scan() {
    // Both layouts, not just the full rail — the compact block autoplays too.
    document.querySelectorAll('.haus-ugc video[data-ugc-src]').forEach(function (v) {
      if (v.dataset.ugcObserved) return;
      v.dataset.ugcObserved = '1';
      observer().observe(v);
    });
    syncAll();
  }

  /* ---------------------------------------------------------------------
     Fullscreen viewer
     --------------------------------------------------------------------- */

  var viewer = null;
  var slides = [];
  var index = 0;
  var lastFocus = null;

  var ICON_CLOSE =
    '<svg viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
  var ICON_PREV =
    '<svg viewBox="0 0 8 14" fill="none" aria-hidden="true"><path d="M7 1L1 7l6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_NEXT =
    '<svg viewBox="0 0 8 14" fill="none" aria-hidden="true"><path d="M1 1l6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function buildViewer() {
    if (viewer) return viewer;
    viewer = document.createElement('div');
    viewer.className = 'haus-ugc-viewer';
    viewer.setAttribute('role', 'dialog');
    viewer.setAttribute('aria-modal', 'true');
    viewer.setAttribute('aria-label', 'Customer videos');
    viewer.innerHTML =
      '<div class="haus-ugc-viewer__stage">' +
      '<video playsinline controls preload="metadata"></video>' +
      '<div class="haus-ugc-viewer__dots"></div>' +
      '<button type="button" class="haus-ugc-viewer__btn haus-ugc-viewer__close" aria-label="Close">' +
      ICON_CLOSE +
      '</button>' +
      '<button type="button" class="haus-ugc-viewer__btn haus-ugc-viewer__nav haus-ugc-viewer__nav--prev" aria-label="Previous video">' +
      ICON_PREV +
      '</button>' +
      '<button type="button" class="haus-ugc-viewer__btn haus-ugc-viewer__nav haus-ugc-viewer__nav--next" aria-label="Next video">' +
      ICON_NEXT +
      '</button>' +
      '</div>';
    document.body.appendChild(viewer);

    viewer.addEventListener('click', function (e) {
      if (closest(e.target, '.haus-ugc-viewer__close')) return closeViewer();
      if (closest(e.target, '.haus-ugc-viewer__nav--prev')) return go(index - 1);
      if (closest(e.target, '.haus-ugc-viewer__nav--next')) return go(index + 1);
      // Backdrop click closes; clicks on the video itself must not.
      if (!closest(e.target, '.haus-ugc-viewer__stage')) closeViewer();
    });

    var startX = null;
    viewer.addEventListener(
      'touchstart',
      function (e) {
        startX = e.touches[0].clientX;
      },
      { passive: true }
    );
    viewer.addEventListener(
      'touchend',
      function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        startX = null;
        if (Math.abs(dx) > 45) go(index + (dx < 0 ? 1 : -1));
      },
      { passive: true }
    );

    return viewer;
  }

  function openViewer(item) {
    var rail = closest(item, ROOT);
    if (!rail) return;
    var items = Array.prototype.slice.call(rail.querySelectorAll('.haus-ugc__item'));
    slides = items.map(function (el) {
      return {
        src: el.dataset.ugcSrc || '',
        hls: el.dataset.ugcHls || '',
        poster: el.dataset.ugcPoster || ''
      };
    });
    index = items.indexOf(item);
    if (index < 0) index = 0;

    lastFocus = document.activeElement;
    var v = buildViewer();
    document.documentElement.classList.add('haus-ugc-lock');
    document.body.classList.add('haus-ugc-lock');
    v.style.display = '';
    render();
    // Next frame, so the opacity transition actually runs.
    requestAnimationFrame(function () {
      v.classList.add('is-open');
    });
    var close = v.querySelector('.haus-ugc-viewer__close');
    if (close) close.focus();
    document.addEventListener('keydown', onKey);
  }

  function render() {
    var v = viewer.querySelector('video');
    var slide = slides[index];
    if (!slide) return;
    clearSources(v);
    v.poster = slide.poster;
    attachSources(v, slide.src, slide.hls);
    var p = v.play();
    if (p && p.catch) p.catch(function () {});

    viewer.querySelector('.haus-ugc-viewer__nav--prev').hidden = index <= 0;
    viewer.querySelector('.haus-ugc-viewer__nav--next').hidden = index >= slides.length - 1;

    var dots = viewer.querySelector('.haus-ugc-viewer__dots');
    dots.innerHTML = slides
      .map(function (_, i) {
        return '<span class="haus-ugc-viewer__dot' + (i === index ? ' is-active' : '') + '"></span>';
      })
      .join('');
    // A single video is not a set; the dot row would just be noise.
    dots.hidden = slides.length < 2;
  }

  function go(next) {
    if (next < 0 || next >= slides.length) return;
    index = next;
    render();
  }

  function onKey(e) {
    if (e.key === 'Escape') closeViewer();
    else if (e.key === 'ArrowRight') go(index + 1);
    else if (e.key === 'ArrowLeft') go(index - 1);
  }

  function closeViewer() {
    if (!viewer) return;
    document.removeEventListener('keydown', onKey);
    // Drop the source so the browser stops buffering a video nobody is watching.
    clearSources(viewer.querySelector('video'));
    viewer.classList.remove('is-open');
    document.documentElement.classList.remove('haus-ugc-lock');
    document.body.classList.remove('haus-ugc-lock');
    var el = viewer;
    setTimeout(function () {
      if (!el.classList.contains('is-open')) el.style.display = 'none';
    }, 200);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
    lastFocus = null;
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  if (document.readyState === 'complete') afterLoad();
  else window.addEventListener('load', afterLoad);

  // Theme editor, and the PDP's own AJAX colourway swap, both replace markup
  // without a page load.
  document.addEventListener('shopify:section:load', scan);

  var mo = new MutationObserver(debounce(scan, 150));
  mo.observe(document.body, { childList: true, subtree: true });
})();
